import React from 'react';
import Link from 'next/link';
import {
  Type,
  Image,
  TextLink,
  StatusIndicator,
  SemanticEssentialColor,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { Identifier } from '@hooks/useAlbumInfo/types';
import { usePagination } from '@hooks/usePagination';
import {
  Filter,
  useShowUnassociatedVideos,
} from '@hooks/useShowUnassociatedVideos';
import { DataTable } from '@components/shared/DataTable';
import { NavigationLink } from '@components/shared/ResultsTable/NavigationLink';
import { AnchorWithHover } from '@components/shared/styles';
import { hasRelatedReleaseIsrc } from '@components/Track/helpers';
import { getArtistTranslationKey } from '@helpers/artistUtils';
import { getUriId } from '@helpers/utils';
import {
  formatUnassociatedVideosData,
  getLoadingSkeletonData,
  UnassociatedVideosFormatted,
} from './helper';
import { UnassociatedVideosExpandedRow } from './UnassociatedVideosExpandedRow';

type Status = {
  text: string;
  color: SemanticEssentialColor;
};

export interface UnassociatedVideosTableProps {
  filter: Filter;
}

export const UnassociatedVideosTable: React.VFC<
  UnassociatedVideosTableProps
> = ({ filter }) => {
  const t = useT();
  const { limit, page, renderPagination } = usePagination();

  const {
    data = { unassociatedVideos: [] },
    status,
    isLoading,
  } = useShowUnassociatedVideos(page, limit, filter, true);

  const getUnassociatedVideosData = (): UnassociatedVideosFormatted[] => {
    if (isLoading) {
      return getLoadingSkeletonData(limit);
    }

    if (status !== 200) {
      return [];
    }

    return formatUnassociatedVideosData(data.unassociatedVideos);
  };

  const unassociatedVideosData = getUnassociatedVideosData();

  const getStatus = (identifiers: Identifier[]): Status => {
    if (hasRelatedReleaseIsrc(identifiers)) {
      return {
        text: t(
          'atlas.related_tracks.errors.failed_association',
          'Failed association',
          'No track associations error state - shortened for table column',
        ),
        color: 'essentialWarning',
      };
    }

    return {
      text: t(
        'atlas.related_tracks.errors.no_association',
        'No association',
        'No direct associations has been made between this track and another  - shortened for table column',
      ),
      color: 'essentialSubdued',
    };
  };

  return (
    <>
      <DataTable
        headers={[
          {
            width: '6%',
            property: 'albumCoverArtImage',
            label: '',
            renderRow: row => (
              <Image
                alt={`${row.trackName} avatar`}
                src={row.albumCoverArtImage}
                crop
                imageHeight="48px"
                imageWidth="48px"
              />
            ),
          },
          {
            width: '21%',
            property: 'trackName',
            label: t(
              'atlas.breadcrumbs.track',
              'Track',
              'Text for a breadcrumb link. Refers to "Track" in the noun form',
            ),
            renderRow: row => (
              <Link
                href={{
                  pathname: `/release/${getUriId(
                    row.albumUri,
                  )}/track/${getUriId(row.uri)}`,
                  query: {
                    fromRelease: row.albumUri,
                  },
                }}
                passHref
                legacyBehavior
              >
                <AnchorWithHover
                  onClick={evt => {
                    evt.stopPropagation();
                  }}
                >
                  <Type variant="viola" semanticColor="textBase">
                    <div>
                      <Type
                        variant="viola"
                        weight="bold"
                        semanticColor="textBrightAccent"
                      >
                        {row.trackName}
                      </Type>
                    </div>
                    <Type variant="viola" semanticColor="textSubdued">
                      {row.artists
                        .map(
                          artist =>
                            `${artist.name} (${getArtistTranslationKey(
                              artist.role,
                              t,
                            )})`,
                        )
                        .join(', ') || ''}
                    </Type>
                  </Type>
                </AnchorWithHover>
              </Link>
            ),
          },
          {
            width: '10%',
            property: 'isrc',
            label: t(
              'atlas.labels.isrc',
              'ISRC',
              'Label for ISRC. Identification tool for sound and music video recordings',
            ),
          },
          {
            width: '20%',
            property: 'uri',
            label: 'URI',
            renderRow: row => (
              <TextLink
                standalone
                href={row.url}
                target="_blank"
                semanticColor="textBrightAccent"
              >
                {row.uri}
              </TextLink>
            ),
          },
          {
            width: '16%',
            property: 'release',
            label: t(
              'atlas.breadcrumbs.release',
              'Release',
              'Text for a breadcrumb link. Refers to "Release" in the noun form',
            ),
            renderRow: row => (
              <NavigationLink albumUri={row.albumUri}>
                <Type
                  variant="viola"
                  weight="bold"
                  semanticColor="textBrightAccent"
                >
                  {row.trackName}
                </Type>
              </NavigationLink>
            ),
          },
          {
            width: '12%',
            property: 'relatedIdentifiers',
            label: t(
              'atlas.labels.status',
              'Status',
              'Label for Status of a release - indicating whether the a related ISRC is present or not',
            ),
            renderRow: row => (
              <StatusIndicator
                semanticColor={getStatus(row.relatedIdentifiers).color}
              >
                <Type variant="viola" semanticColor="textSubdued">
                  {getStatus(row.relatedIdentifiers).text}
                </Type>
              </StatusIndicator>
            ),
          },
          {
            width: '10%',
            property: 'releaseDate',
            label: t(
              'atlas.label.release_date',
              'Release date',
              'Label for the release date of a track. Refers to "Release date" in the noun form',
            ),
          },
        ]}
        data={unassociatedVideosData}
        isLoading={isLoading}
        expandable
        onExpandRow={row => (
          <UnassociatedVideosExpandedRow
            albumUri={row.albumUri}
            trackUri={row.uri}
          />
        )}
      />
      {status === 200 &&
        renderPagination({
          totalResultRows: unassociatedVideosData.length || 0,
        })}
    </>
  );
};
